import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Answers, Phase, QuizStep } from "../types/quiz";
import { sounds } from "../utils/sounds";
import { formatName } from "../utils/name";

export function useQuiz(steps: QuizStep[]) {
  const [phase, setPhase] = useState<Phase>("start");
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState<Answers>({});
  const [name, setName] = useState("");
  const [locked, setLocked] = useState(false);
  const timer = useRef<number>();

  const totalQuestions = useMemo(() => steps.filter((s) => s.kind === "question").length, [steps]);
  const answeredCount = useMemo(
    () => steps.slice(0, index).filter((s) => s.kind === "question").length,
    [steps, index]
  );

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [phase, index]);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const advanceFrom = useCallback(
    (from: number) => {
      if (from >= steps.length - 1) setPhase("loading");
      else setIndex(from + 1);
    },
    [steps.length]
  );

  const start = () => {
    sounds.start();
    setPhase("name");
  };

  const submitName = (value: string) => {
    sounds.tick();
    setName(formatName(value));
    setIndex(0);
    setPhase("quiz");
  };

  const select = (questionId: string, optionId: string) => {
    if (locked) return;
    sounds.tick();
    setAnswers((prev) => ({ ...prev, [questionId]: optionId }));
    setLocked(true);
    const from = index;
    timer.current = window.setTimeout(() => {
      setLocked(false);
      advanceFrom(from);
    }, 320);
  };

  const next = () => {
    sounds.tick();
    advanceFrom(index);
  };

  const finishLoading = useCallback(() => {
    sounds.result();
    setPhase("result");
  }, []);

  return {
    phase,
    step: steps[index],
    answers,
    name,
    totalQuestions,
    answeredCount,
    start,
    submitName,
    select,
    next,
    finishLoading,
  };
}
